import { Injectable }    from '@angular/core';
import {
  Observable,
  Subject
}                        from "rxjs";

import { ConnectionViewerConfiguration } from '@/models/connection.viewer.configuration';
import {
  DarkThemeMode,
  LightThemeMode,
  Theme
}                        from '@/models/appearence/theme';

/**
 *
 * @class ConnectionViewerConfigService
 *
 * @brief tracks the current configuration can publish changes to subscribers.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
@Injectable({ providedIn: 'root' })
export class ConnectionViewerConfigService {

  /** the current configuration for each viewer */
  private config: Map<number, {

    config:        ConnectionViewerConfiguration,
    subject:       Subject<ConnectionViewerConfiguration>,
    subjectFile:   Subject<File | null>,
    subjectResize: Subject<{ width: number, height: number }>

  }> = new Map<number, {

    config:        ConnectionViewerConfiguration,
    subject:       Subject<ConnectionViewerConfiguration>,
    subjectFile:   Subject<File | null>,
    subjectResize: Subject<{ width: number, height: number }>

  }>();

  constructor() {

    //Logger.debug(`[ConfigService] ready.`);

  }

  /** fetch the set of stuff for this viewer */
  private properties(viewer: number): {

    config:        ConnectionViewerConfiguration,
    subject:       Subject<ConnectionViewerConfiguration>,
    subjectFile:   Subject<File | null>,
    subjectResize: Subject<{ width: number, height: number }>

  } {

    let cs: ConnectionViewerConfigService = this;

    let config: {
      config:        ConnectionViewerConfiguration,
      subject:       Subject<ConnectionViewerConfiguration>,
      subjectFile:   Subject<File | null>,
      subjectResize: Subject<{ width: number, height: number }>
    }                     = {
      config:        new ConnectionViewerConfiguration(),
      subject:       new Subject<ConnectionViewerConfiguration>(),
      subjectFile:   new Subject<File | null>(),
      subjectResize: new Subject<{ width: number, height: number }>()
    };

    /* fill in the default if we have to */

    if (!cs.config.has(viewer)) {

      cs.config.set(viewer, config);
    }

    return cs.config.get(viewer) ?? config;
  }

  /** fetch the current configuration for the given viewer */
  private viewerConfiguration(viewer: number): ConnectionViewerConfiguration {

    let cs: ConnectionViewerConfigService = this;

    return cs.properties(viewer).config;
  }

  /** fetch the raw configuration */
  public getConfiguration(viewer: number): ConnectionViewerConfiguration {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config;
  }

  /** helper to remove a viewer when its window closes */
  public removeConfiguration(viewer: number): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;

    cs.config.delete(viewer);

    return this;
  }

  /** force the y-axis scale factor */
  public setScaleY(viewer: number, scaleY: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.scaleY = scaleY;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the y-axis scale factor */
  public getScaleY(viewer: number): number {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.scaleY;
  }

  /** force the x-axis scale factor */
  public setScaleX(viewer: number,scaleX: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.scaleX = scaleX;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the x-axis scale factor */
  public getScaleX(viewer: number): number {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.scaleX;
  }

  /** fetch the current file; if there is one */
  public getFile(viewer: number): File|null {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.file ?? null;
  }

  /** force teh file being rendered */
  public setFile(viewer: number, file?: File, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    //Logger.debug(`[ConfigService] changing file:`, file);

    config.file = file ?? null;

    /* by default we save any changes immediately */

    if (save ?? true) {

      /* file change public */

      cs.properties(viewer).subjectFile.next(config.file);

      /* general change publish */

      cs.save(viewer);
    }

    return cs;
  }

  /** update the canvas width and height at the same time */
  public setWidthHeight(viewer: number, width: number, height: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    //Logger.debug(`[ConfigService] resizing w=${width}, h=${height}`);

    config.width = width;
    config.height = height;

    if (save ?? true) {

      /* resize change publish */

      cs.properties(viewer).subjectResize.next({
        width: config.width,
        height: config.height
      });

      /* general change publish */

      cs.save(viewer);
    }

    return cs;
  }

  /** update the canvas width */
  public setWidth(viewer: number, width: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    //Logger.debug(`[ConfigService] changing width:`, width);

    config.width = width;

    if (save ?? true) {

      /* resize change publish */

      cs.properties(viewer).subjectResize.next({
        width: config.width,
        height: config.height
      });

      /* general change publish */

      cs.save(viewer);
    }

    return cs;
  }

  /** update the canvas height */
  public setHeight(viewer: number, height: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    //Logger.debug(`[ConfigService] changing height:`, height);

    config.height = height;

    if (save ?? true) {

      /* resize change publish */

      cs.properties(viewer).subjectResize.next({
        width: config.width,
        height: config.height
      });

      /* general change publish */

      cs.save(viewer);
    }

    return cs;
  }

  /** subscribe to any changes in actions */
  public onChange(viewer: number): Observable<ConnectionViewerConfiguration> {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return cs.properties(viewer).subject.asObservable();
  }

  /** subscribe to the file changing */
  public onFileChange(viewer: number): Observable<File | null> {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return cs.properties(viewer).subjectFile.asObservable();
  }

  /** subscribe to resizing */
  public onResize(viewer: number): Observable<{ width: number, height: number }> {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return cs.properties(viewer).subjectResize.asObservable();
  }

  /** force the debug mode */
  public setDebugMode(viewer: number, flag: boolean, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.debugMode = flag;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the debug mode */
  public getDebugMode(viewer: number): boolean {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.debugMode;
  }

  /** force the spike lines mode */
  public setSpikeLinesMode(viewer: number, flag: boolean, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.spikeLineMode = flag;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the spike lines mode */
  public getSpikeLinesMode(viewer: number): boolean {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.spikeLineMode;
  }

  /** force the HUD  mode */
  public setHUDMode(viewer: number, flag: boolean, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.hudMode = flag;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the HUD mode */
  public getHUDMode(viewer: number): boolean {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.hudMode;
  }

  /** force the HUD Simple mode */
  public setHUDSimpleMode(viewer: number, flag: boolean, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.hudSimpleMode = flag;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }


  /** fetch the HUD Simple mode */
  public getHUDSimpleMode(viewer: number): boolean {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.hudSimpleMode;
  }

  /** force the dark mode */
  public setDarkMode(viewer: number, flag: boolean, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.darkMode = flag;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the dark mode */
  public getDarkMode(viewer: number): boolean {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.darkMode;
  }


  /** force the navigation bar visibility */
  public setNavVisibility(viewer: number, flag: boolean, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.navMode = flag;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the navigation bar visibility */
  public getNavVisibility(viewer: number): boolean {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.navMode;
  }

  /** fetch the navigation bar visibility */
  public getToolBarHeight(viewer: number): number {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return cs.theme(viewer).toolbar_height;
  }

  /** fetch teh current theme (depends on being in dark or light mode) */
  public theme(viewer: number): Theme {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    if (config.darkMode) {

      return cs.getDarkModeTheme(viewer);
    }

    return cs.getLightModeTheme(viewer);
  }

  /** force the dark mode theme */
  public setDarkModeTheme(viewer: number, darkTheme: Theme, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.darkTheme = darkTheme;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the dark mode theme */
  public getDarkModeTheme(viewer: number): Theme {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.darkTheme ?? Theme.dark();
  }

  /** force the light mode theme */
  public setLightModeTheme(viewer: number, lightTheme: Theme, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.lightTheme = lightTheme;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** fetch the light mode theme */
  public getLightModeTheme(viewer: number): Theme {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    return config.lightTheme ?? Theme.light();
  }

  /** toggle dark / light mode */
  public toggleDark(viewer: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    config.darkMode = !config.darkMode;

    if (save ?? true) {

      cs.save(viewer);
    }

    return cs;
  }

  /** rotate the active (dark or light) mode theme */
  public rotateTheme(viewer: number): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService     = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    if (config.darkMode) {

      cs.rotateDarkTheme(viewer);

      return cs;
    }

    cs.rotateLightTheme(viewer);

    return cs;
  }

  /** rotate the dark theme */
  public rotateDarkTheme(viewer: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    if (config.darkTheme) {

      config.darkTheme = Theme.dark();
    }

    let oldMode: DarkThemeMode = config.darkTheme.darkMode;
    let modes: DarkThemeMode[] = [
      DarkThemeMode.DARK,
      DarkThemeMode.DARK1,
      DarkThemeMode.DARK2,
      DarkThemeMode.DARK3,
      DarkThemeMode.DARK4,
      DarkThemeMode.DARK5,
    ];

    let position: number    = modes.indexOf(oldMode);

    if (position < 0) {

      position = -1;
    }

    if (position >= (modes.length - 1)) {

      position = -1;
    }

    let newMode: DarkThemeMode = modes[position + 1];

    //Logger.debug(`[ConfigService] rotating dark theme mode: old=${oldMode}, new=${newMode}...`);

    return cs.setDarkModeTheme(viewer, Theme.buildDarkTheme(newMode));
  }

  /** rotate the light theme */
  public rotateLightTheme(viewer: number, save?: boolean): ConnectionViewerConfigService {

    let cs: ConnectionViewerConfigService = this;
    let config: ConnectionViewerConfiguration = cs.viewerConfiguration(viewer);

    if (!config.lightTheme) {

      config.lightTheme = Theme.light();
    }

    let oldMode: LightThemeMode = config.lightTheme.lightMode;
    let modes: LightThemeMode[] = [
      LightThemeMode.LIGHT,
      LightThemeMode.LIGHT1,
      LightThemeMode.LIGHT2,
      LightThemeMode.LIGHT3,
      LightThemeMode.LIGHT4,
      LightThemeMode.LIGHT5,
    ];

    let position: number    = modes.indexOf(oldMode);

    if (position < 0) {

      position = -1;
    }

    if (position >= (modes.length - 1)) {

      position = -1;
    }

    let newMode: LightThemeMode = modes[position + 1];

    //Logger.debug(`[ConfigService] rotating light theme mode: old=${oldMode}, new=${newMode}...`);

    return cs.setLightModeTheme(viewer, Theme.buildLightTheme(newMode));
  }

  /** "save" andy changes, publish to all subscribers */
  public save(viewer: number): void {

    let cs: ConnectionViewerConfigService = this;
    let properties: {

      config:        ConnectionViewerConfiguration,
      subject:       Subject<ConnectionViewerConfiguration>,
      subjectFile:   Subject<File | null>,
      subjectResize: Subject<{ width: number, height: number }>

    } = cs.properties(viewer);

    /* publish change */

    properties.subject.next(properties.config);
  }

}
